import { useEffect, useState } from "react"
import { Platform, View, ScrollView, TouchableOpacity } from "react-native"
import { GestureHandlerRootView } from "react-native-gesture-handler"
import { BottomSheetModalProvider } from "@gorhom/bottom-sheet"
import CloseButton from "@/assets/icons/close-button.svg"
import CustomCompPills from "@/components/customizecomps/CustomCompPills"
import CustomCompSlider from "@/components/customizecomps/CustomCompSlider"
import CustomCompSliderRange from "@/components/customizecomps/CustomCompSliderRange"

import { useViewedCallback } from "@/analytics/react"
import { setStatusBarStyle } from "@/stores/statusBarStyle"
import BFText from "@/components/standard/BFText"
import {
  DISTANCES_MSA,
  // DISTANCES_NON_MSA,
  SOLD_DATES,
  LISTING_STATUSES,
  generateSqFtOptions,
  generateBedOptions,
  generateBathOptions,
} from "@/utils/CustomizeCompsOptions"
import {
  customizeCompsViewed,
  customizeCompsExitWithoutSaving,
  customizeCompsSubmitted,
  customizeCompsResetToDefaults,
} from "@/analytics/events/customizecomps"

import SquareFeetIcon from "@/assets/icons/custom-comp-icon-sqft.svg"
import BedsIcon from "@/assets/icons/custom-comp-icon-bed.svg"
import BathsIcon from "@/assets/icons/custom-comp-icon-bath.svg"
import FullScreenLoader from "@/components/loading/FullScreenLoaderDark"
import { numberWithCommas } from "@/utils/NumberUtils"
import { useAnalysisData } from "@/hooks/useAnalysisData"
import {
  type AnalysisCompInput,
  type CompQueryPreferenceType,
} from "@/generated/graphql"
import { router } from "expo-router"
import { useUpdateCompPreferences } from "@/queries/analysis"
import { LightBackedBottomButton } from "@/components/modals/LightBackedBottomButton"

export default function CustomizeCompsScreen() {
  const [distance, setDistance] = useState<number | null>(null)
  const [daysSinceSold, setDaysSinceSold] = useState<number | null>(null)
  const [listingStatuses, setListingStatuses] = useState<string[]>([])
  const [minSqft, setMinSqft] = useState<number | null>(null)
  const [maxSqft, setMaxSqft] = useState<number | null>(null)
  const [minBeds, setMinBeds] = useState<number | null>(null)
  const [maxBeds, setMaxBeds] = useState<number | null>(null)
  const [minBaths, setMinBaths] = useState<number | null>(null)
  const [maxBaths, setMaxBaths] = useState<number | null>(null)

  const {
    analysis,
    isAnalysisLoading,
    analysisId,
    defaultCompPreferences,
    defaultsAreLoading,
  } = useAnalysisData()

  const { mutate, isPending } = useUpdateCompPreferences()

  const compPreferences: CompQueryPreferenceType | null =
    analysis?.compQueryPreference || null

  useEffect(() => {
    setDistance(compPreferences?.distance || 1)
    setDaysSinceSold(compPreferences?.daysSinceSold || 365)
    setListingStatuses(
      compPreferences?.listingStatuses || [
        "sold",
        "active",
        "contingent",
        "pending",
      ]
    )
    setMinSqft(compPreferences?.areaMin || analysis?.property.area || null)
    setMaxSqft(compPreferences?.areaMax || analysis?.property.area || null)
    setMinBeds(compPreferences?.bedsMin || analysis?.property.bedrooms || null)
    setMaxBeds(compPreferences?.bedsMax || analysis?.property.bedrooms || null)
    setMinBaths(compPreferences?.bathsMin || 0)
    setMaxBaths(
      compPreferences?.bathsMax || analysis?.property.bathrooms || null
    )
  }, [analysis, compPreferences])

  useViewedCallback(() => {
    setStatusBarStyle("dark")
    customizeCompsViewed()
  })

  if (isAnalysisLoading || !analysis) {
    return <FullScreenLoader>Loading...</FullScreenLoader>
  }

  const onClearAll = () => {
    if (!defaultCompPreferences) {
      return
    }

    const previousValues = getPayload()
    const defaultValues: AnalysisCompInput = defaultCompPreferences

    setDistance(defaultValues.distance || 1)
    setDaysSinceSold(defaultValues.daysSinceSold || 365)
    setListingStatuses(
      (defaultValues.listingStatuses as string[]) || [
        "sold",
        "active",
        "contingent",
        "pending",
      ]
    )
    setMinSqft(defaultValues.areaMin || null)
    setMaxSqft(defaultValues.areaMax || null)
    setMinBeds(defaultValues.bedsMin || null)
    setMaxBeds(defaultValues.bedsMax || null)
    setMinBaths(defaultValues.bathsMin || 0)
    setMaxBaths(defaultValues.bathsMax || null)

    customizeCompsResetToDefaults({
      analysisId: analysis.id,
      previousValues,
      currentValues: defaultValues,
    })
  }

  const getPayload = (): AnalysisCompInput => ({
    distance,
    areaMax: maxSqft,
    areaMin: minSqft,
    bathsMax: maxBaths,
    bathsMin: minBaths,
    bedsMax: maxBeds,
    bedsMin: minBeds,
    daysSinceSold,
    listingStatuses,
  })

  const onSave = () => {
    const payload: AnalysisCompInput = getPayload()
    customizeCompsSubmitted(payload)
    const preferencesInput = {
      analysisId,
      analysisCompInput: payload,
    }

    mutate(preferencesInput, {
      onSuccess: () => {
        // console.log("Preferences successfully saved.")
        router.push("../") // Navigate after successful save
      },
      onError: (error) => {
        console.error("Error saving preferences:", error)
      },
    })
  }

  const { minOptions: minSqftOptions, maxOptions: maxSqftOptions } =
    generateSqFtOptions(analysis.area || 0, 65, 185)

  const { minOptions: minBedOptions, maxOptions: maxBedOptions } =
    generateBedOptions(analysis.bedrooms || 0)

  const { minOptions: minBathOptions, maxOptions: maxBathOptions } =
    generateBathOptions(analysis.bathrooms || 0)

  return (
    <GestureHandlerRootView style={{ flex: 1, backgroundColor: "white" }}>
      <BottomSheetModalProvider>
        <View className="flex flex-col flex-1 bg-off-white p-6">
          <View
            className="flex items-end justify-end"
            style={{
              height: Platform.OS === "web" ? 20 : 70,
            }}
          >
            <TouchableOpacity
              testID={"close-button"}
              onPress={() => {
                customizeCompsExitWithoutSaving({
                  currentScreen: "Customize Comps",
                  toScreen: "Subject Property",
                })
                router.push("../")
              }}
              hitSlop={32}
            >
              <CloseButton width={13} height={13} />
            </TouchableOpacity>
          </View>
          <ScrollView
            className="flex flex-col flex-1 pt-6"
            showsVerticalScrollIndicator={false}
          >
            <View className="flex flex-row flex-1 items-center mb-6">
              <BFText className="text-midnight-blue text-[18px] font-figtree-bold mr-4">
                Filter
              </BFText>

              <TouchableOpacity
                testID={"reset-button"}
                style={{ opacity: defaultsAreLoading ? 0.1 : 1.0 }}
                disabled={defaultsAreLoading}
                onPress={onClearAll}
              >
                <BFText className="text-calm-blue text-[14px] underline">
                  Reset to Default
                </BFText>
              </TouchableOpacity>
            </View>

            <CustomCompSlider
              title="Distance"
              value={distance}
              options={DISTANCES_MSA} // You can switch between DISTANCES_MSA and DISTANCES_NON_MSA based on the return
              onValueChanged={(val) => {
                setDistance(typeof val === "number" ? val : null)
              }}
              measurementUnit={"mile"}
            />

            <CustomCompSlider
              title="Sold Date"
              value={daysSinceSold}
              options={SOLD_DATES}
              onValueChanged={(val) => {
                // Convert val to a number and handle non-numeric inputs gracefully
                const numericValue = Number(val)
                setDaysSinceSold(isNaN(numericValue) ? 365 : numericValue)
              }}
              bottomDescription={""}
            />

            <CustomCompPills
              title="Listing Status"
              subtitle="Deselect statuses not needed"
              values={listingStatuses}
              options={LISTING_STATUSES}
              onValuesChanged={setListingStatuses}
            />

            <CustomCompSliderRange
              title="Square Feet"
              subjectValue={analysis.area || 0}
              label={`${numberWithCommas(analysis.area || 0)} sq ft`}
              sublabel={"SUBJECT PROPERTY"}
              minOptions={minSqftOptions}
              maxOptions={maxSqftOptions}
              initialMin={compPreferences?.areaMin || analysis?.property.area}
              initialMax={compPreferences?.areaMax || analysis?.property.area}
              stepSize={100}
              onValuesChanged={(minVal, maxVal) => {
                setMinSqft(minVal)
                setMaxSqft(maxVal)
              }}
              middleIcon={<SquareFeetIcon />}
              descriptionType="Percentage"
            />

            <CustomCompSliderRange
              title="Beds"
              subjectValue={analysis.bedrooms || 0}
              label={`${numberWithCommas(analysis.bedrooms || 0)} Beds`}
              sublabel={"SUBJECT PROPERTY"}
              minOptions={minBedOptions}
              maxOptions={maxBedOptions}
              initialMin={
                compPreferences?.bedsMin || analysis?.property.bedrooms
              }
              initialMax={
                compPreferences?.bedsMax || analysis?.property.bedrooms
              }
              onValuesChanged={(minVal, maxVal) => {
                setMinBeds(minVal)
                setMaxBeds(maxVal)
              }}
              middleIcon={<BedsIcon />}
              descriptionType="Standard"
            />

            <CustomCompSliderRange
              title="Baths"
              subjectValue={analysis.bathrooms || 0}
              label={`${numberWithCommas(analysis.bathrooms || 0)} Baths`}
              sublabel={"SUBJECT PROPERTY"}
              minOptions={minBathOptions}
              maxOptions={maxBathOptions}
              initialMin={compPreferences?.bathsMin || 0}
              initialMax={
                compPreferences?.bathsMax || analysis?.property.bathrooms
              }
              onValuesChanged={(minVal, maxVal) => {
                setMinBaths(minVal)
                setMaxBaths(maxVal)
              }}
              middleIcon={<BathsIcon />}
              descriptionType="Standard"
            />

            <View className="flex h-[120px]" />
          </ScrollView>
          <LightBackedBottomButton
            testID={"save-button"}
            title="Save"
            onPress={onSave}
            loading={isPending}
          />
        </View>
      </BottomSheetModalProvider>
    </GestureHandlerRootView>
  )
}
